// src/components/admin/AdminHealth.jsx
// Platform Health — Phase 4

import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../utils/api';

// ── Helpers ───────────────────────────────────────────────────
const fmtTime = d => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

const HEALTH_MOBILE_CSS = `
@media (max-width: 768px) {
  /* Header bar: stack status + meta + button */
  .health-header      { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
  .health-header-meta { flex-wrap: wrap; gap: 6px; }
  .health-header-sep  { display: none !important; }
  .health-refresh-btn { width: 100% !important; justify-content: center; }

  /* DB + Memory panels: stack to 1-col */
  .health-db-mem-grid { grid-template-columns: 1fr !important; }

  /* Table rows inside DB panel: 2-col */
  .health-table-counts { grid-template-columns: 1fr 1fr !important; }

  /* Error log: hide Route/Message columns, keep Code+Method+Time */
  .health-err-col-route    { display: none !important; }
  .health-err-col-message  { display: none !important; }
  .health-err-hd-route     { display: none !important; }
  .health-err-hd-message   { display: none !important; }
  .health-err-row          { gap: 8px !important; }
}
@media (max-width: 400px) {
  .health-mem-gauge { flex-direction: column !important; align-items: center !important; }
}
`;

function InjectStyle({ id, css }) {
  useEffect(() => {
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id; el.textContent = css;
    document.head.appendChild(el);
  }, []);
  return null;
}

function Spinner() {
  return <div style={{ padding: 60, textAlign: 'center', color: 'var(--gray)', fontSize: 13 }}>Loading health data…</div>;
}

// ── Status dot ────────────────────────────────────────────────
function StatusDot({ status }) {
  const color = status === 'healthy' ? 'var(--green)' : status === 'degraded' ? 'var(--warning)' : 'var(--red)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: `0 0 6px ${color}`, animation: status === 'healthy' ? 'pulseDot 2s ease infinite' : 'none', flexShrink: 0 }}/>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color }}>{status}</span>
    </span>
  );
}

// ── Metric tile ───────────────────────────────────────────────
function Tile({ label, value, sub, color = 'var(--gold)', icon }) {
  return (
    <div style={{ background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${color}, transparent 70%)` }}/>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--gray)' }}>{label}</span>
        {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
      </div>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

// ── Memory gauge ──────────────────────────────────────────────
function MemGauge({ pct, usedMB, totalMB }) {
  const color = pct > 85 ? 'var(--red)' : pct > 65 ? 'var(--warning)' : 'var(--green)';
  const R = 42, circ = 2 * Math.PI * R;
  const dash = (pct / 100) * circ;
  return (
    <div className="health-mem-gauge" style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
      <svg width={100} height={100} viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
        <circle cx={50} cy={50} r={R} fill="none" stroke="rgba(255,255,255,.06)" strokeWidth={10}/>
        <circle cx={50} cy={50} r={R} fill="none" stroke={color} strokeWidth={10}
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeDashoffset={circ / 4}
          style={{ transition: 'stroke-dasharray .6s ease' }}
        />
        <text x={50} y={46} textAnchor="middle" fontSize="16" fontWeight="700" fill="var(--white)" fontFamily="'Bebas Neue',sans-serif">{pct}%</text>
        <text x={50} y={60} textAnchor="middle" fontSize="8" fill="var(--gray)" fontFamily="'Inter',sans-serif">HEAP</text>
      </svg>
      <div>
        <div style={{ fontSize: 12, color: 'var(--off-white)', marginBottom: 6 }}>Heap Usage</div>
        <div style={{ fontSize: 13, fontWeight: 700, color }}>{usedMB} MB used</div>
        <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>of {totalMB} MB allocated</div>
      </div>
    </div>
  );
}

// ── DB latency bar ────────────────────────────────────────────
function LatencyBar({ ms }) {
  const max   = 500;
  const pct   = Math.min((ms / max) * 100, 100);
  const color = ms < 50 ? 'var(--green)' : ms < 200 ? 'var(--warning)' : 'var(--red)';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: 'var(--off-white)' }}>DB Response Time</span>
        <span style={{ fontSize: 13, fontWeight: 700, color, fontFamily: "'Bebas Neue',sans-serif", letterSpacing: '.03em' }}>{ms}ms</span>
      </div>
      <div style={{ height: 8, background: 'rgba(255,255,255,.06)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width .5s ease' }}/>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontSize: 9, color: 'var(--gray)' }}>0ms</span>
        <span style={{ fontSize: 9, color: 'var(--green)' }}>Good &lt;50ms</span>
        <span style={{ fontSize: 9, color: 'var(--warning)' }}>Slow &gt;200ms</span>
        <span style={{ fontSize: 9, color: 'var(--gray)' }}>500ms</span>
      </div>
    </div>
  );
}

// ── Error row ─────────────────────────────────────────────────
function ErrorRow({ err }) {
  const is5xx = err.statusCode >= 500;
  return (
    <div className="health-err-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
      <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, color: is5xx ? 'var(--red)' : 'var(--warning)', width: 36, flexShrink: 0 }}>{err.statusCode}</span>
      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray)', width: 38, flexShrink: 0 }}>{err.method}</span>
      <span className="health-err-col-route" style={{ fontSize: 11, color: 'var(--off-white)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{err.route}</span>
      <span className="health-err-col-message" style={{ fontSize: 11, color: 'var(--gray)', flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{err.message}</span>
      <span style={{ fontSize: 10, color: 'var(--gray)', flexShrink: 0, whiteSpace: 'nowrap' }}>{fmtTime(err.ts)}</span>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function AdminHealth() {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh,setLastRefresh]= useState(null);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const d = await adminApi.getHealth();
      setData(d);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <Spinner/>;
  if (error)   return <div style={{ padding: 40, color: 'var(--red)', fontSize: 13 }}>Failed to load health data: {error}</div>;

  const { db, memory, tables, errors, uptime, node, status, serverStart } = data;

  const tableRows = [
    { label: 'Users',           value: tables.users,           icon: '👤' },
    { label: 'Trades',          value: tables.trades,          icon: '📈' },
    { label: 'Referrals',       value: tables.referrals,       icon: '🔗' },
    { label: 'Notebooks',       value: tables.notebooks,       icon: '📓' },
    { label: 'Refresh Tokens',  value: tables.refreshTokens,   icon: '🔑' },
    { label: 'AI Conversations',value: tables.aiConversations, icon: '🤖' },
  ];

  return (
    <>
    <InjectStyle id="health-mobile-css" css={HEALTH_MOBILE_CSS}/>
    <div style={{ padding: '14px 18px 60px', animation: 'pageIn .3s ease', display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Header bar */}
      <div className="health-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 18px' }}>
        <div className="health-header-meta" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <StatusDot status={status}/>
          <div className="health-header-sep" style={{ width: 1, height: 16, background: 'rgba(255,255,255,.1)' }}/>
          <span style={{ fontSize: 12, color: 'var(--gray)' }}>Up since <span style={{ color: 'var(--off-white)' }}>{fmtTime(serverStart)}</span></span>
          <div className="health-header-sep" style={{ width: 1, height: 16, background: 'rgba(255,255,255,.1)' }}/>
          <span style={{ fontSize: 12, color: 'var(--gray)' }}>Node <span style={{ color: 'var(--off-white)' }}>{node.version}</span> · {node.platform} · <span style={{ color: node.env === 'production' ? 'var(--green)' : 'var(--warning)' }}>{node.env}</span></span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastRefresh && <span style={{ fontSize: 10, color: 'var(--gray)' }}>Updated {fmtTime(lastRefresh)}</span>}
          <button className="health-refresh-btn" onClick={() => load(true)} disabled={refreshing} style={{ padding: '6px 14px', background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.2)', color: 'var(--gold)', fontSize: 11, fontWeight: 700, letterSpacing: '.05em', cursor: refreshing ? 'not-allowed' : 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 'var(--radius)', opacity: refreshing ? .6 : 1, textTransform: 'uppercase' }}>
            {refreshing ? '…' : '↺ Refresh'}
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <Tile label="Uptime"       value={uptime}               color="var(--green)"  icon="⏱" sub="Since last restart"/>
        <Tile label="DB Latency"   value={`${db.latencyMs}ms`}  color={db.latencyMs < 50 ? 'var(--green)' : db.latencyMs < 200 ? 'var(--warning)' : 'var(--red)'} icon="🗄" sub={db.status}/>
        <Tile label="5xx Errors"   value={errors.count5xx}      color={errors.count5xx > 0 ? 'var(--red)' : 'var(--green)'} icon="⚠" sub="In error buffer"/>
        <Tile label="4xx Errors"   value={errors.count4xx}      color={errors.count4xx > 10 ? 'var(--warning)' : 'var(--green)'} icon="⚡" sub="In error buffer"/>
        <Tile label="Heap Memory"  value={`${memory.pct}%`}     color={memory.pct > 85 ? 'var(--red)' : 'var(--green)'} icon="💾" sub={`${memory.heapUsedMB}/${memory.heapTotalMB} MB`}/>
        <Tile label="RSS Memory"   value={`${memory.rssMB}MB`}  color="var(--blue)"   icon="📦" sub="Total process memory"/>
      </div>

      {/* DB + Memory row */}
      <div className="health-db-mem-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="panel">
          <div className="panel-hd">
            <div className="panel-title"><b>Database</b> Performance</div>
            <StatusDot status={db.status}/>
          </div>
          <LatencyBar ms={db.latencyMs}/>
          <div className="health-table-counts" style={{ marginTop: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {tableRows.map(({ label, value, icon }) => (
              <div key={label} style={{ background: 'var(--dark3)', padding: '10px 12px', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14 }}>{icon}</span>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--gray)', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>{label}</div>
                  <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, color: 'var(--off-white)', lineHeight: 1.1 }}>{value.toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="panel">
          <div className="panel-hd">
            <div className="panel-title"><b>Memory</b> Usage</div>
          </div>
          <MemGauge pct={memory.pct} usedMB={memory.heapUsedMB} totalMB={memory.heapTotalMB}/>
          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { label: 'Heap Used',  value: `${memory.heapUsedMB} MB`,  color: 'var(--gold)'  },
              { label: 'Heap Total', value: `${memory.heapTotalMB} MB`, color: 'var(--off-white)' },
              { label: 'RSS',        value: `${memory.rssMB} MB`,       color: 'var(--blue)'  },
              { label: 'External',   value: `${memory.externalMB} MB`,  color: 'var(--gray)'  },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                <span style={{ fontSize: 12, color: 'var(--gray)' }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color, fontFamily: "'Bebas Neue',sans-serif", letterSpacing: '.02em' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Error log */}
      <div className="panel">
        <div className="panel-hd">
          <div className="panel-title"><b>Error</b> Log <span style={{ fontSize: 11, color: 'var(--gray)', fontWeight: 400 }}>(last {errors.recent.length} of {errors.total})</span></div>
          <div style={{ display: 'flex', gap: 8 }}>
            {errors.count5xx > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--red)',     background: 'rgba(217,79,79,.1)',  border: '1px solid rgba(217,79,79,.2)',  padding: '2px 8px', borderRadius: 3 }}>{errors.count5xx} 5xx</span>}
            {errors.count4xx > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--warning)', background: 'rgba(251,191,36,.1)', border: '1px solid rgba(251,191,36,.2)', padding: '2px 8px', borderRadius: 3 }}>{errors.count4xx} 4xx</span>}
          </div>
        </div>
        {!errors.recent.length ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--green)', fontSize: 13 }}>✓ No errors recorded since server start</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, padding: '6px 0 10px', borderBottom: '1px solid rgba(255,255,255,.06)', marginBottom: 4 }}>
              {[['Code',null], ['Method',null], ['Route','health-err-hd-route'], ['Message','health-err-hd-message'], ['Time',null]].map(([h, cls], i) => (
                <span key={h} className={cls||''} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--gray)', flexShrink: i < 2 ? 0 : i === 4 ? 0 : 1, width: i === 0 ? 36 : i === 1 ? 38 : 'auto' }}>{h}</span>
              ))}
            </div>
            {errors.recent.map((e, i) => <ErrorRow key={i} err={e}/>)}
          </>
        )}
      </div>

    </div>
    </>
  );
}